import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, ArrowLeft } from "lucide-react";
import marbleKitchen from "@/assets/marble-kitchen.jpg";
import marbleSink from "@/assets/marble-sink.jpg";
import marbleCounter from "@/assets/marble-counter.jpg";
import marbleTable from "@/assets/marble-table.jpg";
import marbleDecor from "@/assets/marble-decor.jpg";

const MarbleProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, isRTL } = useLanguage();

  const content = {
    ar: {
      backToProducts: "العودة لمنتجات الرخام",
      contactUs: "تواصل معنا للطلب",
      features: "المميزات",
      ourProjects: "مشاريعنا وأعمالنا",
      products: {
        "kitchen-surfaces": {
          title: "أسطح المطابخ الرخامية",
          description: "تصنيع أسطح رخامية فاخرة للمطابخ",
          longDescription: "نقدم تصنيع أسطح رخامية فاخرة للمطابخ بتصاميم عصرية وألوان متنوعة من الرخام الصناعي عالي الجودة. مقاومة للخدوش والبقع وسهلة التنظيف مع عمر افتراضي طويل.",
          features: ["مقاومة للخدوش", "مقاومة للبقع", "سهولة التنظيف", "ألوان متنوعة", "عمر طويل"],
          images: [marbleKitchen, marbleSink, marbleCounter]
        },
        "sinks": {
          title: "المغاسل الرخامية",
          description: "تصنيع مغاسل رخامية بتصاميم أنيقة",
          longDescription: "تصنيع مغاسل رخامية بتصاميم أنيقة للحمامات والمطابخ من الرخام الصناعي بمواصفات عالية ومقاومة للرطوبة. تشكيلة متنوعة من الأشكال والألوان.",
          features: ["مقاومة للرطوبة", "تصاميم أنيقة", "أشكال متنوعة", "سطح ناعم", "صيانة سهلة"],
          images: [marbleSink, marbleKitchen, marbleCounter]
        },
        "counters": {
          title: "كونترات المحلات",
          description: "تصنيع كونترات وأسطح عمل من الرخام الصناعي",
          longDescription: "تصنيع كونترات وأسطح عمل من الرخام الصناعي للمحلات التجارية والمكاتب بمواصفات عالية ومتانة فائقة. مثالية للاستخدام التجاري المكثف.",
          features: ["متانة فائقة", "مقاومة للاستخدام المكثف", "مظهر احترافي", "مقاسات مخصصة", "تركيب سريع"],
          images: [marbleCounter, marbleTable, marbleKitchen]
        },
        "tables": {
          title: "طاولات رخامية",
          description: "تصنيع طاولات رخامية للمنازل والمطاعم والمكاتب",
          longDescription: "تصنيع طاولات رخامية للمنازل والمطاعم والمكاتب بتصاميم فريدة وخيارات متعددة من الرخام الصناعي. تجمع بين الفخامة والعملية.",
          features: ["تصاميم فريدة", "فخامة عالية", "مقاومة للصدمات", "خيارات متعددة", "جودة عالية"],
          images: [marbleTable, marbleDecor, marbleKitchen]
        },
        "decorations": {
          title: "ديكورات رخامية",
          description: "تصنيع قطع ديكور وتحف من الرخام الصناعي",
          longDescription: "تصنيع قطع ديكور وتحف من الرخام الصناعي منحوتة بدقة لإضفاء لمسة فخامة على المنازل والمكاتب. أعمال فنية مخصصة حسب الطلب.",
          features: ["نحت دقيق", "أعمال فنية", "تصاميم مخصصة", "لمسة فخامة", "جودة استثنائية"],
          images: [marbleDecor, marbleTable, marbleSink]
        }
      }
    },
    en: {
      backToProducts: "Back to Marble Products",
      contactUs: "Contact Us to Order",
      features: "Features",
      ourProjects: "Our Projects & Work",
      products: {
        "kitchen-surfaces": {
          title: "Marble Kitchen Surfaces",
          description: "Manufacturing luxury marble surfaces for kitchens",
          longDescription: "We offer manufacturing of luxury marble surfaces for kitchens with modern designs and diverse colors from high-quality artificial marble. Scratch and stain resistant, easy to clean with a long lifespan.",
          features: ["Scratch resistant", "Stain resistant", "Easy to clean", "Diverse colors", "Long lifespan"],
          images: [marbleKitchen, marbleSink, marbleCounter]
        },
        "sinks": {
          title: "Marble Sinks",
          description: "Manufacturing marble sinks with elegant designs",
          longDescription: "Manufacturing marble sinks with elegant designs for bathrooms and kitchens from artificial marble with high specifications and moisture resistance. Diverse selection of shapes and colors.",
          features: ["Moisture resistant", "Elegant designs", "Various shapes", "Smooth surface", "Easy maintenance"],
          images: [marbleSink, marbleKitchen, marbleCounter]
        },
        "counters": {
          title: "Shop Counters",
          description: "Manufacturing counters and work surfaces from artificial marble",
          longDescription: "Manufacturing counters and work surfaces from artificial marble for commercial shops and offices with high specifications and superior durability. Ideal for intensive commercial use.",
          features: ["Superior durability", "Intensive use resistant", "Professional appearance", "Custom sizes", "Quick installation"],
          images: [marbleCounter, marbleTable, marbleKitchen]
        },
        "tables": {
          title: "Marble Tables",
          description: "Manufacturing marble tables for homes, restaurants, and offices",
          longDescription: "Manufacturing marble tables for homes, restaurants, and offices with unique designs and multiple options from artificial marble. Combining luxury and practicality.",
          features: ["Unique designs", "High luxury", "Impact resistant", "Multiple options", "High quality"],
          images: [marbleTable, marbleDecor, marbleKitchen]
        },
        "decorations": {
          title: "Marble Decorations",
          description: "Manufacturing decorative pieces and artifacts from artificial marble",
          longDescription: "Manufacturing decorative pieces and artifacts from artificial marble precisely carved to add a touch of luxury to homes and offices. Custom artwork according to request.",
          features: ["Precise carving", "Artwork", "Custom designs", "Touch of luxury", "Exceptional quality"],
          images: [marbleDecor, marbleTable, marbleSink]
        }
      }
    }
  };

  const t = content[language];
  const product = slug ? t.products[slug as keyof typeof t.products] : null;

  if (!product) {
    return (
      <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">{language === "ar" ? "المنتج غير موجود" : "Product Not Found"}</h1>
          <Link to="/marble">
            <Button className="bg-primary hover:bg-primary/90">{t.backToProducts}</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <Link to="/marble" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-8 font-kufi">
          <ArrowIcon className="w-4 h-4" />
          {t.backToProducts}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-12">
          <h1 className="text-4xl font-kufi font-bold text-primary mb-4">{product.title}</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{product.description}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="bg-card rounded-xl shadow-lg p-8 mb-12">
          <p className="text-lg leading-relaxed text-card-foreground">{product.longDescription}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="bg-primary/5 rounded-xl p-8 mb-12">
          <h2 className="text-2xl font-kufi font-bold text-primary mb-6">{t.features}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {product.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 bg-card p-4 rounded-lg shadow-sm">
                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                <span className="text-card-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="mb-12">
          <h2 className="text-2xl font-kufi font-bold text-primary mb-8 text-center">{t.ourProjects}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.images.map((image, index) => (
              <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: index * 0.1 }} className="group relative overflow-hidden rounded-xl shadow-lg aspect-[4/3]">
                <img src={image} alt={`${product.title} ${index + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className="text-center bg-primary/10 rounded-xl p-8">
          <Link to="/contact">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-kufi text-lg px-8">{t.contactUs}</Button>
          </Link>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default MarbleProductDetail;
